import React from 'react'
import { Field, ErrorMessage } from 'formik'

export const CenterInformationForm: React.FC = () => {
  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-2xl leading-6 font-bold text-[#3C2416]">
          Basic Center Information
        </h3>
        <p className="mt-2 text-sm text-[#142841]">
          Please fill in the requested information about your sickle cell
          disease center. All fields marked with an asterisk (*) are required.
        </p>
      </div>

      <div className="px-4 py-5 sm:px-6">
        <div className="grid grid-cols-6 gap-6">
          {/* Center Name */}
          <div className="col-span-6">
            <label
              htmlFor="centerName"
              className="block text-sm font-medium text-[#3C2416] mb-2"
            >
              Center Name *
            </label>
            <Field
              type="text"
              name="centerName"
              id="centerName"
              data-testid="center-name-input"
              autoComplete="organization"
              className="block w-full rounded-lg border-0 py-3 px-4 text-[#142841] shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#A6410A] sm:text-sm sm:leading-6 transition-all duration-200"
              placeholder="Enter center name"
            />
            <ErrorMessage
              name="centerName"
              component="p"
              className="mt-1 text-sm text-red-600"
              data-testid="center-name-error"
            />
          </div>

          {/* Street Address */}
          <div className="col-span-6">
            <label
              htmlFor="streetAddress"
              className="block text-sm font-medium text-[#3C2416] mb-2"
            >
              Street Address *
            </label>
            <Field
              type="text"
              name="streetAddress"
              id="streetAddress"
              data-testid="street-address-input"
              autoComplete="street-address"
              className="block w-full rounded-lg border-0 py-3 px-4 text-[#142841] shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#A6410A] sm:text-sm sm:leading-6 transition-all duration-200"
              placeholder="Enter street address"
            />
            <ErrorMessage
              name="streetAddress"
              component="p"
              className="mt-1 text-sm text-red-600"
            />
          </div>

          {/* City, State, Zip */}
          <div className="col-span-6 sm:col-span-2">
            <label
              htmlFor="city"
              className="block text-sm font-medium text-[#3C2416] mb-2"
            >
              City *
            </label>
            <Field
              type="text"
              name="city"
              id="city"
              data-testid="city-input"
              autoComplete="address-level2"
              className="block w-full rounded-lg border-0 py-3 px-4 text-[#142841] shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#A6410A] sm:text-sm sm:leading-6 transition-all duration-200"
              placeholder="Enter city"
            />
            <ErrorMessage
              name="city"
              component="p"
              className="mt-1 text-sm text-red-600"
            />
          </div>

          <div className="col-span-6 sm:col-span-2">
            <label
              htmlFor="state"
              className="block text-sm font-medium text-[#3C2416] mb-2"
            >
              State / Province *
            </label>
            <Field
              type="text"
              name="state"
              id="state"
              data-testid="state-input"
              autoComplete="address-level1"
              className="block w-full rounded-lg border-0 py-3 px-4 text-[#142841] shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#A6410A] sm:text-sm sm:leading-6 transition-all duration-200"
              placeholder="Enter state"
            />
            <ErrorMessage
              name="state"
              component="p"
              className="mt-1 text-sm text-red-600"
            />
          </div>

          <div className="col-span-6 sm:col-span-2">
            <label
              htmlFor="zipCode"
              className="block text-sm font-medium text-[#3C2416] mb-2"
            >
              ZIP / Postal Code *
            </label>
            <Field
              type="text"
              name="zipCode"
              id="zipCode"
              data-testid="zip-code-input"
              autoComplete="postal-code"
              className="block w-full rounded-lg border-0 py-3 px-4 text-[#142841] shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#A6410A] sm:text-sm sm:leading-6 transition-all duration-200"
              placeholder="Enter ZIP code"
            />
            <ErrorMessage
              name="zipCode"
              component="p"
              className="mt-1 text-sm text-red-600"
            />
          </div>

          {/* Director Information */}
          <div className="col-span-6">
            <h4 className="text-lg font-semibold text-[#3C2416] mb-4 pt-4 border-t border-gray-200">
              Director Information
            </h4>
          </div>

          <div className="col-span-6 sm:col-span-3">
            <label
              htmlFor="directorFirstName"
              className="block text-sm font-medium text-[#3C2416] mb-2"
            >
              Director&apos;s First Name *
            </label>
            <Field
              type="text"
              name="directorFirstName"
              id="directorFirstName"
              data-testid="director-first-name-input"
              autoComplete="given-name"
              className="block w-full rounded-lg border-0 py-3 px-4 text-[#142841] shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#A6410A] sm:text-sm sm:leading-6 transition-all duration-200"
              placeholder="Enter first name"
            />
            <ErrorMessage
              name="directorFirstName"
              component="p"
              className="mt-1 text-sm text-red-600"
            />
          </div>

          <div className="col-span-6 sm:col-span-3">
            <label
              htmlFor="directorLastName"
              className="block text-sm font-medium text-[#3C2416] mb-2"
            >
              Director&apos;s Last Name *
            </label>
            <Field
              type="text"
              name="directorLastName"
              id="directorLastName"
              data-testid="director-last-name-input"
              autoComplete="family-name"
              className="block w-full rounded-lg border-0 py-3 px-4 text-[#142841] shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#A6410A] sm:text-sm sm:leading-6 transition-all duration-200"
              placeholder="Enter last name"
            />
            <ErrorMessage
              name="directorLastName"
              component="p"
              className="mt-1 text-sm text-red-600"
            />
          </div>

          <div className="col-span-6 sm:col-span-4">
            <label
              htmlFor="directorEmail"
              className="block text-sm font-medium text-[#3C2416] mb-2"
            >
              Director&apos;s Email Address *
            </label>
            <Field
              type="email"
              name="directorEmail"
              id="directorEmail"
              data-testid="director-email-input"
              autoComplete="email"
              className="block w-full rounded-lg border-0 py-3 px-4 text-[#142841] shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#A6410A] sm:text-sm sm:leading-6 transition-all duration-200"
              placeholder="director@example.com"
            />
            <ErrorMessage
              name="directorEmail"
              component="p"
              className="mt-1 text-sm text-red-600"
            />
          </div>

          {/* Co-Director Information */}
          <div className="col-span-6">
            <h4 className="text-lg font-semibold text-[#3C2416] mb-4 pt-4 border-t border-gray-200">
              Co-Director Information{' '}
              <span className="text-sm font-normal text-gray-500">
                (Optional)
              </span>
            </h4>
          </div>

          <div className="col-span-6 sm:col-span-3">
            <label
              htmlFor="coDirectorFirstName"
              className="block text-sm font-medium text-[#3C2416] mb-2"
            >
              Co-Director&apos;s First Name
            </label>
            <Field
              type="text"
              name="coDirectorFirstName"
              id="coDirectorFirstName"
              autoComplete="given-name"
              className="block w-full rounded-lg border-0 py-3 px-4 text-[#142841] shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#A6410A] sm:text-sm sm:leading-6 transition-all duration-200"
              placeholder="Enter first name"
            />
            <ErrorMessage
              name="coDirectorFirstName"
              component="p"
              className="mt-1 text-sm text-red-600"
            />
          </div>

          <div className="col-span-6 sm:col-span-3">
            <label
              htmlFor="coDirectorLastName"
              className="block text-sm font-medium text-[#3C2416] mb-2"
            >
              Co-Director&apos;s Last Name
            </label>
            <Field
              type="text"
              name="coDirectorLastName"
              id="coDirectorLastName"
              autoComplete="family-name"
              className="block w-full rounded-lg border-0 py-3 px-4 text-[#142841] shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#A6410A] sm:text-sm sm:leading-6 transition-all duration-200"
              placeholder="Enter last name"
            />
            <ErrorMessage
              name="coDirectorLastName"
              component="p"
              className="mt-1 text-sm text-red-600"
            />
          </div>

          <div className="col-span-6 sm:col-span-4">
            <label
              htmlFor="coDirectorEmail"
              className="block text-sm font-medium text-[#3C2416] mb-2"
            >
              Co-Director&apos;s Email Address
            </label>
            <Field
              type="email"
              name="coDirectorEmail"
              id="coDirectorEmail"
              autoComplete="email"
              className="block w-full rounded-lg border-0 py-3 px-4 text-[#142841] shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#A6410A] sm:text-sm sm:leading-6 transition-all duration-200"
              placeholder="codirector@example.com"
            />
            <ErrorMessage
              name="coDirectorEmail"
              component="p"
              className="mt-1 text-sm text-red-600"
            />
          </div>

          {/* Contact Information */}
          <div className="col-span-6">
            <h4 className="text-lg font-semibold text-[#3C2416] mb-4 pt-4 border-t border-gray-200">
              Contact Information
            </h4>
          </div>

          <div className="col-span-6 sm:col-span-5">
            <label
              htmlFor="primaryCenterContactEmail"
              className="block text-sm font-medium text-[#3C2416] mb-2"
            >
              Primary Contact Email *
            </label>
            <Field
              type="email"
              name="primaryCenterContactEmail"
              id="primaryCenterContactEmail"
              data-testid="primary-contact-email-input"
              autoComplete="email"
              className="block w-full rounded-lg border-0 py-3 px-4 text-[#142841] shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#A6410A] sm:text-sm sm:leading-6 transition-all duration-200"
              placeholder="contact@example.com"
            />
            <p className="mt-2 text-sm text-gray-500">
              This should be the email of the person who should receive
              important correspondence for the center.
            </p>
            <ErrorMessage
              name="primaryCenterContactEmail"
              component="p"
              className="mt-1 text-sm text-red-600"
            />
          </div>

          <div className="col-span-6 sm:col-span-5">
            <label
              htmlFor="websiteAddress"
              className="block text-sm font-medium text-[#3C2416] mb-2"
            >
              Center Website *
            </label>
            <div className="flex rounded-lg shadow-sm">
              <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                https://
              </span>
              <Field
                type="text"
                name="websiteAddress"
                id="websiteAddress"
                data-testid="website-input"
                className="flex-1 min-w-0 block w-full px-3 py-3 rounded-none rounded-r-lg focus:ring-2 focus:ring-inset focus:ring-[#A6410A] sm:text-sm border-gray-300 transition-all duration-200"
                placeholder="www.example.com"
              />
            </div>
            <ErrorMessage
              name="websiteAddress"
              component="p"
              className="mt-1 text-sm text-red-600"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
